import {
  LeagueGroupFragment,
  SeasonLeagueFragment,
  TeamListItemFragment,
  TeamRetributionItemFragment,
} from '@amf/shared/types/graphql';
import useLeagueTable from '@amf/shared/data/hooks/useLeagueTable';
import { useTranslate } from '@tolgee/react';
import Loader from '@amf/shared/components/utils/Loader';
import LeagueTeamTable from '@amf/shared/components/league/LeagueTeamTable';
import { TeamTableFilterData } from '@amf/shared/components/team/TeamTableFilter';
import { LeaguePhaseType, LeagueTeamHomeOrAway } from '@amf/shared/data/League';
import React, { useMemo } from 'react';
import TeamRetribution from '@amf/shared/components/team/TeamRetribution';
import MatchPlayOffTable from '@amf/shared/components/match/MatchPlayOffTable';
import FormattedDate from '@amf/shared/components/utils/FormattedDate';

type Retribution = {
  team: TeamListItemFragment;
  retribution: TeamRetributionItemFragment;
};

interface TeamTableGroupProps {
  group: LeagueGroupFragment;
  prefixes: { team: string };
  title?: boolean;
  disableWonInExtension?: boolean;
}

function TeamTableGroup({ group, title, prefixes, disableWonInExtension }: TeamTableGroupProps) {
  const retributions = useMemo<Retribution[]>(() => {
    const result: Retribution[] = [];
    for (const team of group.teams) {
      for (const retribution of team.retributions) {
        result.push({
          team: team.team,
          retribution,
        });
      }
    }
    return result;
  }, [group]);

  return (
    <>
      {title && <h2 className='TeamTable__title'>{group.name}</h2>}
      <div className='TeamTable'>
        <LeagueTeamTable
          teams={group.teams}
          labels={group.labels}
          prefix={prefixes.team}
          champion={group.champion}
          advancing={group.advancing}
          descending={group.descending}
          disableWonInExtension={disableWonInExtension}
        />
        {retributions.length > 0 && (
          <div className='TeamTable__retributions'>
            {retributions.map(({ team, retribution }) => (
              <p key={retribution.id}>
                <i className='bi bi-info-circle-fill' />
                <TeamRetribution team={team} retribution={retribution} />
              </p>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

interface TeamTableProps {
  league: SeasonLeagueFragment;
  prefixes: {
    team: string;
    match: string;
  };
  filter?: TeamTableFilterData;
  disableWonInExtension?: boolean;
}

function TeamTable({ league, prefixes, filter, disableWonInExtension }: TeamTableProps) {
  const { t } = useTranslate();
  const { table, loading, updatedAt } = useLeagueTable(
    league.id,
    filter?.homeOrAway ?? LeagueTeamHomeOrAway.all,
    filter?.phase ?? null,
  );

  const phase = useMemo(() => {
    if (!filter) return null;
    return league.phases.find(p => p.id === filter.phase) ?? null;
  }, [filter, league.phases]);

  if (loading) {
    return <Loader>{t('table.list.loading')}</Loader>;
  }

  if (
    phase?.type === LeaguePhaseType.playOff ||
    phase?.type === LeaguePhaseType.custom ||
    phase?.type === LeaguePhaseType.advancedPlayOff
  ) {
    return (
      <MatchPlayOffTable
        prefixes={prefixes}
        groups={table}
        numberOfMutualMatches={phase.numberOfMutualMatches}
      />
    );
  }

  return (
    <>
      {table.map(group => (
        <TeamTableGroup
          key={group.id}
          group={group}
          prefixes={prefixes}
          title={table.length > 1}
          disableWonInExtension={disableWonInExtension}
        />
      ))}
      {updatedAt && (
        <p className='TeamTable__update'>
          {t('table.lastUpdate')}{' '}
          <FormattedDate date={updatedAt} format='dd.MM.yyyy HH:mm' type='browser' />.
        </p>
      )}
    </>
  );
}

export default TeamTable;
